import { useEffect, useRef } from 'react'

import { useProcurifyIntl } from '@webapp/hooks'
import { ConversationThinkingIndicator } from './ConversationThinkingIndicator'
import messages from './messages'
import { renderConversationItem } from './renderConversationItem'
import {
  ConversationStreamItemStyled,
  ConversationStreamListStyled,
} from './styles/ConversationStreamStyled'
import {
  type ConversationCatalogItemComponent,
  type ConversationChoiceOption,
  type ConversationTurnItem,
} from '../../../../value-objects/ConversationTurnItem'
import { ConversationItemKinds } from '../../../../value-objects/enums'

const isUserMessage = (item: ConversationTurnItem): boolean =>
  item.kind === ConversationItemKinds.MESSAGE && item.sender === 'user'

export interface IConversationStreamProps {
  items: ConversationTurnItem[]
  isStreaming: boolean
  isSubmitting?: boolean
  thinkingLabel?: Nullable<string>
  selectedCatalogItemId?: Nullable<string>
  onChoiceSelect: (option: ConversationChoiceOption) => void
  onSendMessage: (message: string) => void
  onCatalogItemSelect: (item: ConversationCatalogItemComponent) => void
  getCatalogItemQuantity: (item: ConversationCatalogItemComponent) => number
  onCatalogItemQuantityChange: (
    item: ConversationCatalogItemComponent,
    quantity: number
  ) => void
  onCatalogItemAddToRequest: (item: ConversationCatalogItemComponent) => void
}

export const ConversationStream = ({
  items,
  isStreaming,
  isSubmitting = false,
  thinkingLabel = null,
  selectedCatalogItemId = null,
  onChoiceSelect,
  onSendMessage,
  onCatalogItemSelect,
  getCatalogItemQuantity,
  onCatalogItemQuantityChange,
  onCatalogItemAddToRequest,
}: IConversationStreamProps) => {
  const intl = useProcurifyIntl()
  const feedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    feedRef.current?.scrollIntoView?.({ block: 'end' })
  }, [items])

  useEffect(() => {
    if (isStreaming) {
      feedRef.current?.scrollIntoView?.({ block: 'end' })
    }
  }, [isStreaming, thinkingLabel])

  const lastItem = items[items.length - 1]
  const isAfterResponse = lastItem !== undefined && !isUserMessage(lastItem)

  if (items.length === 0 && !isStreaming) {
    return null
  }

  return (
    <ConversationStreamListStyled
      ref={feedRef}
      role='log'
      aria-live='polite'
      aria-label={intl.formatMessage(messages.feedAriaLabel)}
    >
      {items.map((item) => (
        <ConversationStreamItemStyled
          key={item.id}
          $isUserTurn={isUserMessage(item)}
        >
          {renderConversationItem(item, {
            isStreaming,
            isSubmitting,
            selectedCatalogItemId,
            onChoiceSelect,
            onSendMessage,
            onCatalogItemSelect,
            getCatalogItemQuantity,
            onCatalogItemQuantityChange,
            onCatalogItemAddToRequest,
          })}
        </ConversationStreamItemStyled>
      ))}
      {isStreaming && (
        <ConversationThinkingIndicator
          label={thinkingLabel}
          isAfterResponse={isAfterResponse}
        />
      )}
    </ConversationStreamListStyled>
  )
}
